import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import { Box, Card, Grid, Skeleton } from "@mui/material";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { AxiosResponse } from "axios";
import { serviceFetchBankOffers } from "src/api/service/services";
import defaultImage from "../../assets/default_logo_retailor.png"; // ✅ fallback image
import { getRetailerLogo } from "src/utils/retailers_list";

const BankOffers: React.FC = () => {
  const [bankOffersDealData, setBankOffersDealData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    serviceFetchBankOffers()
      .then((response: AxiosResponse) => {
        const validDeals = (response?.data || [])
          // ✅ Keep only deals that have a valid URL
          .filter(
            (deal: any) =>
              deal?.acf?.bank_offers_url &&
              deal?.acf?.bank_offers_url.trim() !== ""
          )
          // ✅ Replace missing/empty images with default
          .map((deal: any) => {
            const image = deal?.acf?.bank_offers_image;
            if (image == null || image.trim() === "") {
              deal.acf.bank_offers_image = defaultImage;
            }
            return deal;
          });

        setBankOffersDealData(validDeals);
      })
      .finally(() => setLoading(false));
  }, []);

  // 🧠 Dynamically set slidesToShow (2 if only two deals, else 3)
  const slidesToShow = bankOffersDealData.length === 2 ? 2 : 3;

  const settings = {
    dots: false,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 0,
    speed: 4000,
    cssEase: "linear",
    pauseOnHover: true,
    slidesToShow,
    slidesToScroll: 1,
    arrows: false,
    responsive: [
      { breakpoint: 900, settings: { slidesToShow: 2 } },
      { breakpoint: 600, settings: { slidesToShow: 1 } },
    ],
  };

  // 🟡 Show skeleton only while loading and before data arrives
  if (loading && bankOffersDealData.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rounded" height={200} animation="wave" />
      </Box>
    );
  }

  // 🔴 Hide everything if no valid data after loading
  if (!loading && bankOffersDealData.length === 0) {
    return null;
  }

  // 🟢 Render slider when valid deals exist
  return (
    <Box sx={{ p: 2 }}>
      <Slider {...settings}>
        {bankOffersDealData.map((deal: any, i: number) => {
          const logoSrc = getRetailerLogo(
            deal?.acf?.bank_offers_retailer || ""
          );

          return (
            <Box key={i} sx={{ mx: 1.5 }}>
              <Card
                id={`bank-offer-${deal.id}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "16px",
                  overflow: "hidden",
                  background: "linear-gradient(90deg, #ffffff 0%, #f7f7f7 100%)",
                  boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.04)",
                    boxShadow: "0px 6px 20px rgba(0,0,0,0.15)",
                  },
                }}
              >
                <Grid container alignItems="center">
                  {/* 🏦 Logo Section */}
                  <Grid
                    item
                    xs={4}
                    sm={3}
                    md={3}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      background:
                        "linear-gradient(135deg, #fff 30%, rgba(240,240,240,0.8))",
                      height: 150,
                      p: 2,
                    }}
                  >
                    <Box
                      component="img"
                      src={logoSrc}
                      alt={`${deal?.acf?.bank_offers_retailer} logo`}
                      sx={{
                        width: "90%",
                        height: "50%",
                        objectFit: "contain",
                        borderRadius: "12px",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        backgroundColor: "white",
                        p: 1,
                      }}
                    />
                  </Grid>

                  {/* 💳 Offer Image Section */}
                  <Grid item xs={8} sm={9} md={9}>
                    <Box
                      component="img"
                      src={deal?.acf?.bank_offers_image}
                      alt={deal?.acf?.bank_offers_alt || `bank-offer-${i + 1}`}
                      onClick={() =>
                        window.open(deal?.acf.bank_offers_url, "_blank")
                      }
                      sx={{
                        width: "100%",
                        height: 150,
                        objectFit: "contain",
                        cursor: deal?.acf.bank_offers_url
                          ? "pointer"
                          : "default",
                        borderRadius: "0 16px 16px 0",
                      }}
                    />
                  </Grid>
                </Grid>
              </Card>
            </Box>
          );
        })}
      </Slider>
    </Box>
  );
};

export default BankOffers;
